import { cn } from "@/lib/utils";
import { isChunkLoadError, reloadOnceForNewVersion } from "@/lib/chunkReload";
import i18n from "@/i18n";
import { AlertTriangle, LifeBuoy, RotateCcw } from "lucide-react";
import { Component, ReactNode } from "react";

// Страница /support требует входа — незалогиненному оставляем почту.
const SUPPORT_EMAIL = "judixkz@gmail.com";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  /** Ошибка из-за выката новой версии — страница перезагружается сама. */
  updating: boolean;
}

/**
 * Английская строка зашита в код: экран может появиться до того, как
 * подгрузятся переводы, и тогда человек всё равно поймёт, что делать.
 */
const EN = {
  updatingTitle: "We've updated JUDIX",
  updatingBody: "A new version is out — the page is refreshing itself.",
  title: "The page needs a refresh",
  body: "This happens after a platform update. Nothing is lost — press «Reload». If it repeats, contact support.",
  reload: "Reload the page",
  support: "Contact support",
  details: "Details for support",
};

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, updating: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, updating: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error) {
    // Выкатили новую версию — у открытой вкладки пропали её куски приложения.
    // Перезагружаем сами, показывать человеку нечего.
    if (isChunkLoadError(error)) reloadOnceForNewVersion();
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const isEnglish = (i18n.language || "ru").toLowerCase().startsWith("en");
    const t = (key: keyof typeof EN) => {
      const value = i18n.t(`errorBoundary.${key}`, { ns: "common", defaultValue: EN[key] });
      return typeof value === "string" ? value : EN[key];
    };
    // Язык интерфейса — крупно, английский строкой ниже. Если интерфейс и так
    // английский, дублировать нечего.
    const second = (key: keyof typeof EN) => (isEnglish ? null : EN[key]);

    if (this.state.updating) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-8">
          <div className="flex max-w-md flex-col items-center text-center">
            <RotateCcw size={40} className="mb-5 animate-spin text-primary" />
            <h2 className="mb-2 text-lg font-semibold">{t("updatingTitle")}</h2>
            <p className="text-sm text-muted-foreground">{t("updatingBody")}</p>
            {second("updatingTitle") && (
              <p className="mt-3 text-xs text-muted-foreground/80">
                {second("updatingTitle")}. {second("updatingBody")}
              </p>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="flex w-full max-w-md flex-col items-center text-center">
          <AlertTriangle size={40} className="mb-5 shrink-0 text-amber-500" />

          <h2 className="mb-2 text-lg font-semibold">{t("title")}</h2>
          <p className="text-sm text-muted-foreground">{t("body")}</p>
          {second("title") && (
            <p className="mt-3 text-xs text-muted-foreground/80">
              {second("title")}. {second("body")}
            </p>
          )}

          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <button
              onClick={() => window.location.reload()}
              className={cn(
                "flex items-center gap-2 rounded-lg px-4 py-2",
                "bg-primary text-primary-foreground",
                "cursor-pointer hover:opacity-90"
              )}
            >
              <RotateCcw size={16} />
              {t("reload")}
            </button>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="flex items-center gap-2 rounded-lg border px-4 py-2 text-sm hover:bg-muted"
            >
              <LifeBuoy size={16} />
              {t("support")}
            </a>
          </div>

          {/* Технические подробности нужны только поддержке — прячем. */}
          <details className="mt-6 w-full text-left">
            <summary className="cursor-pointer text-xs text-muted-foreground">
              {t("details")}
            </summary>
            <pre className="mt-2 max-h-48 overflow-auto rounded bg-muted p-3 text-[11px] whitespace-break-spaces text-muted-foreground">
              {this.state.error?.stack || this.state.error?.message}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
